import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MenuItem, MessageService, SelectItem, SelectItemGroup } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { deepUpdate } from "@bbwt/utils";
import { Message } from "@bbwt/classes";
import { DisplayMode, IGridColumn, SortOrder } from "@features/grid";
import { IGridDisplayView, IGridView, IGridViewColumn } from "../widget-grid.models";
import { WidgetGridBuilderService } from "../api/widget-grid-builder.service";
import {
    DataType,
    InputType,
    IQuerySchema,
    IQuerySchemaColumn,
    IViewMetadata,
    IViewMetadataColumn,
    IWidgetSource,
    QueryAccessLevel
} from "../../core/reporting-models";
import { WidgetGridDataService } from "../api/widget-grid-data.service";
import { IWidgetBuilder } from "@main/reporting.v3/core/widgets/widget-builder";
import { QueryBuilderComponent } from "../../query-builder/components/query-builder.component";
import { WidgetGridViewService } from "../api/widget-grid-view.service";
import { Clipboard } from "@angular/cdk/clipboard";

@Component({
    selector: "widget-grid-builder",
    templateUrl: "./widget-grid-builder.component.html",
    styleUrls: ["./widget-grid-builder.component.scss"]
})
export class WidgetGridBuilderComponent implements IWidgetBuilder, OnInit {
    // Grid settings.
    gridPreview: IGridDisplayView;
    columnOptions: SelectItem[] = [];
    textAlignOptions: SelectItem[] = [];

    // Grid data.
    querySchema: IQuerySchema;
    viewMetadata: IViewMetadata;
    columns: IGridViewColumn[] = [];
    tableColumns: IGridColumn[] = [];
    private tempActionItems: Array<{ id?: string; name: string; url: string }> = [];
    private deletedActionItemIds: string[] = [];
    private baseWidgetGridId: string;

    // Grid configuration.
    xlsSheetName: string;
    isRowSelectable: boolean;
    defaultSortOrder: SortOrder;
    summaryFooterVisible: boolean;
    defaultSortColumnAlias: string;
    showVisibleColumnsSelector: boolean;

    // Column edition settings.
    editViewColumn: IGridViewColumn;
    editGridViewColumnDialogVisible: boolean;
    dateFormatOptions: MenuItem[] = [];

    // Footer aggregations settings.
    editAggregateExpression: string;
    selectedAggregateExpressionIndex: number;
    footerAggregateColumnOptions: SelectItem[];
    footerAggregateFunctionOptions: SelectItem[];

    // Tooltip settings.
    xlsSheetNameTooltip: string;
    customExpressionTooltip: string;
    dateDisplayFormatTooltip: string;
    customOutputFormatTooltip: string;

    // General settings.
    activeIndex: number = 0;
    loading: boolean = false;

    private _gridView: IGridView;
    private _querySourceId: string;
    private _widgetSource: IWidgetSource = { widgetType: "table" } as IWidgetSource;
    public currentGridId!: string;

    protected readonly QueryAccessLevel = QueryAccessLevel;
    private originalActionItems: Array<{ id?: string; name: string; url: string }> = [];

    @Input() cssInjectionScopeId: string;

    @Input() querySourceOptions: SelectItemGroup[];

    @Output() readonly widgetSourceChange: EventEmitter<IWidgetSource> = new EventEmitter<IWidgetSource>();

    @ViewChild("editGridViewColumnForm", { static: false }) editGridViewColumnForm: NgForm;
    @ViewChild("editGridViewSettingsForm", { static: false }) private editGridViewSettingsForm: NgForm;
    @ViewChild("editGridViewColumnAggregationsForm", { static: false }) editGridViewColumnAggregationsForm: NgForm;
    @ViewChild("overlayPanelColumnOptions", { static: false }) private overlayPanelColumnOptions: OverlayPanel;
    @ViewChild("customAggregateExpressionInput", { static: false }) private aggregateExpressionInput: ElementRef;
    @ViewChild(QueryBuilderComponent, { static: false }) private queryBuilder: QueryBuilderComponent;

    constructor(private widgetGridBuilderService: WidgetGridBuilderService,
                private widgetGridDataService: WidgetGridDataService,
                private widgetGridViewService: WidgetGridViewService,
                private messageService: MessageService,
                private clipboard: Clipboard) {
    }


    // Query source id for creation purposes.
    @Input() set querySourceId(value: string) {
        this._querySourceId = value;
        this.refreshQuery().then();
    }

    get querySourceId(): string {
        return this._querySourceId;
    }

    // Grid view for updating purposes.
    @Input() set gridView(value: IGridView) {
        this._gridView = value ?? { widgetSource: { widgetType: "table" }, columns: [] } as IGridView;
        this._widgetSource = this._gridView.widgetSource;
        this._querySourceId = this._gridView.querySourceId;
        this.currentGridId = this._gridView?.id;
        this.widgetSourceChange.emit(this._widgetSource);
        this.refreshWidget().then();
    }

    get gridView(): IGridView {
        return this._gridView;
    }

    // Widget source id for updating purposes.
    @Input() set widgetSourceId(value: string) {
        if (value === this.widgetSourceId) return;
        if (!!value) {
            this.widgetGridBuilderService.getView(value)
                .then(view => this.gridView = view)
                .catch(error => this.messageService.add(Message.Error(error.message, "Error loading grid")));
        } else this.gridView = null;
    }

    get widgetSourceId(): string {
        return this._widgetSource?.id;
    }

    // One direction accessors to simplify logic.
    get queryBuilderTabActive(): boolean {
        return this.activeIndex === 1;
    }

    get queryBuilderDisabled(): boolean {
        return !this.queryBuilder || this.queryBuilder.disabled;
    }

    get queryBuilderDirty(): boolean {
        return !!this.queryBuilder?.dirty;
    }

    get isDraftWidget(): boolean {
        return !!this._widgetSource?.isDraft;
    }

    get isDraftQuery(): boolean {
        return !!this.queryBuilder?.isDraftQuery;
    }

    get valid(): boolean {
        return !!this.querySourceId
            && (!this.editGridViewSettingsForm || this.editGridViewSettingsForm?.valid);
    }

    ngOnInit(): void {
        this.tableColumns = [
            { field: "queryAlias", header: "Column name" },
            { field: "header", header: "Displayed name" },
            { field: "aggregations", header: "Aggregations" },
            { field: "sortable", header: "Sortable" },
            { field: "visible", header: "Visible" }
        ];

        this.textAlignOptions = [
            { label: "Left", value: "Left" },
            { label: "Center", value: "Center" },
            { label: "Right", value: "Right" }
        ];

        this.xlsSheetNameTooltip = "Set a custom sheet name to export this grid to a specific sheet in the Excel file.\n" +
            "By default, each grid is exported to a separate sheet. To export multiple grids to the same sheet, " +
            "enter the same custom sheet name for each grid.\nNote: If multiple grids share the same sheet name, " +
            "they will be placed in the corresponding sheet in the same order as the grid widgets appear in the layout."

        this.customExpressionTooltip =
            "Use '@column' text to embed current column's value into SQL expression, e.g. SUM(@column). " +
            "Use '@TableName.ColumnName' text for a specific table column, e.g. SUM(@Orders.Total)";

        this.customOutputFormatTooltip =
            "Use {0}, {1} ... identifiers to embed selected expressions into the cell value text. " +
            "See needed identifiers shown on the left of each listed expression ({0}: MIN(...) etc.)";

        this.dateDisplayFormatTooltip = "Use values supported by the moment.js";

        const menuItem = (label: string, format: string) => {
            return <MenuItem>{
                label: `<div class="flex w-28rem">
                            <p class="m-0 w-16rem">${label}</p>
                            <p class="m-0 w-12rem font-bold">${format}</p>
                        <div>`,
                command: _ => {
                    if (!this.editViewColumn) return;
                    this.editViewColumn.extraSettings["dateMomentFormat"] = format;
                },
                escape: false,
            }
        }

        this.dateFormatOptions = [
            menuItem("Local Aware Date:", "L"),
            menuItem("Local Aware Long Date:", "LL"),
            menuItem("Local Aware Long Date and Time:", "LLL"),
            menuItem("Local Aware Full Date and Time:", "LLLL"),
            menuItem("Local Aware Time:", "LT"),
            menuItem("Local Aware Time with Seconds:", "LTS"),
            menuItem("Day:", "DD"),
            menuItem("Month:", "MM"),
            menuItem("Month Name:", "MMMM"),
            menuItem("Year:", "YY"),
            menuItem("Full Year:", "YYYY"),
            menuItem("Day/Month/Year:", "DD/MM/YY"),
            menuItem("Day/Month/Full Year:", "DD/MM/YYYY"),
            menuItem("Day Month Name Full Year:", "DD MMMM YYYY"),
            menuItem("Day-Month-Year Time:", "DD-MM-YY HH:mm:ss"),
            menuItem("Full Year-Month-Day Time:", "YYYY-MM-DD HH:mm:ss"),
        ]
    }

    // Refreshing methods.
    private async refreshQuery(): Promise<void> {
        if (!this.querySourceId) return;

        this.querySchema = await this.widgetGridDataService.getQuerySchema(this.querySourceId);
        this.viewMetadata = await this.widgetGridDataService.getViewMetadata(this.querySourceId);

        // If there is some change in the columns, then also refresh column definition.
        if (!this.columns || !!this.querySchema?.columns?.length) {
            // Check if there is some new column in new query schema definition, using query alias.
            const newColumns: boolean = this.querySchema?.columns.some(first =>
                !this.columns.find(second => first.queryAlias === second.queryAlias));

            // Check if there is some column missing in new query schema definition, using query alias.
            const removedColumns: boolean = this.columns.some(first =>
                !this.querySchema?.columns.find(second => first.queryAlias === second.queryAlias));

            // If there are new columns or some column was removed, refresh grid query columns.
            if (newColumns || removedColumns) this.refreshGridColumns();
        }

        this.refreshPreview();
    }

    private async refreshWidget(): Promise<void> {
        if (!this.querySourceId) return;

        this.querySchema = await this.widgetGridDataService.getQuerySchema(this.querySourceId);
        this.viewMetadata = await this.widgetGridDataService.getViewMetadata(this.querySourceId);

        this.refreshMetadata();
        this.refreshGridColumns();
        this.refreshPreview();
    }

    private refreshMetadata(): void {
        if (!this.gridView) return;

        this.xlsSheetName = this.gridView.xlsSheetName;
        this.isRowSelectable = this.gridView.isRowSelectable;
        this.defaultSortOrder = this.gridView.defaultSortOrder;
        this.defaultSortColumnAlias = this.gridView.defaultSortColumnAlias;
        this.showVisibleColumnsSelector = this.gridView.showVisibleColumnsSelector;
    }

    private refreshGridColumns(): void {
        if (!this.gridView?.columns && !this.querySchema?.columns) return;

        const schemaColumns: IQuerySchemaColumn[] = this.querySchema.columns;
        const metadataColumns: IViewMetadataColumn[] = this.viewMetadata.columns;

        const viewColumns: IGridViewColumn[] = !!this.gridView ? this.gridView.columns : [];

        // Restore sort order of the columns.
        viewColumns.sort((first, second) => first.sortOrder - second.sortOrder);

        // Remove grid columns not present in the schema
        // (grid columns whose query alias do not belong to any query schema column).
        const buildColumns: IGridViewColumn[] = viewColumns
            .filter(column => schemaColumns
                .some(schemaColumn => column.queryAlias === schemaColumn.queryAlias));

        // Get missing schema columns (query schema columns whose query alias do not belong to any grid column).
        this.createMissingColumns(buildColumns, viewColumns, schemaColumns, metadataColumns);

        // Fix custom format of each column to change old query alias references to the new ones if possible.
        this.fixCustomFormatReferences(buildColumns, viewColumns);

        const baseColumnName = "ActionMenu";
        const existingPrefix = this.querySchema?.columns?.[0]?.queryAlias?.split(".")?.[0];
        const queryAlias = existingPrefix ? `${existingPrefix}.${baseColumnName}` : `Reporting_${this.querySourceId}.${baseColumnName}`;

        if (!buildColumns.some(col => col.queryAlias === baseColumnName)) {
            buildColumns.push({
                queryAlias: queryAlias,
                columnName: baseColumnName,
                header: "Actions",
                dataType: "string",
                footer: {},
                visible: true,
                sortable: true,
                inputType: "text",
                displayMode: DisplayMode.Text,
                extraSettings: {
                    actionItems: []
                }
            } as IGridViewColumn);
        }

        // Fix column sort order.
        buildColumns.forEach((item, index) => item.sortOrder = index);

        this.columns = buildColumns;

        this.columnOptions = this.columns.map(column => <SelectItem>{
            label: column.queryAlias,
            value: column.queryAlias
        });
    }

    protected refreshPreview(): void {
        if (this.activeIndex !== 5 || !this.querySourceId) return;

        this.gridPreview = {
            id: this.gridView?.id,
            columns: this.columns,
            xlsSheetName: this.xlsSheetName,
            widgetSource: this._widgetSource,
            querySourceId: this.querySourceId,
            isRowSelectable: this.isRowSelectable,
            widgetSourceId: this._widgetSource?.id,
            defaultSortOrder: this.defaultSortOrder,
            summaryFooterVisible: this.summaryFooterVisible,
            defaultSortColumnAlias: this.defaultSortColumnAlias,
            showVisibleColumnsSelector: this.showVisibleColumnsSelector,
            queryVariables: []
        }
    }

    // Column editing methods.
    private createMissingColumns(buildColumns: IGridViewColumn[], viewColumns: IGridViewColumn[],
                                 schemaColumns: IQuerySchemaColumn[], metadataColumns: IViewMetadataColumn[]): void {
        // Get missing schema columns.
        // (query schema columns whose query alias do not belong to any grid column).
        const missingColumns: IQuerySchemaColumn[] = schemaColumns
            .filter(schemaColumn => !buildColumns
                .some(column => column.queryAlias === schemaColumn.queryAlias));

        // Convert missing columns to grid view columns, and add them to grid view.
        buildColumns.push(...missingColumns.map(schemaColumn => {

            const metadataColumn: IViewMetadataColumn = metadataColumns
                .find(metadataColumn => metadataColumn.queryAlias === schemaColumn.queryAlias);

            const columnName: string = schemaColumn.baseColumnName ?? schemaColumn.columnName;

            const similarColumn: IGridViewColumn = viewColumns
                .find(column => columnName?.localeCompare(column.columnName) === 0);

            return this.createGridViewColumn(schemaColumn, similarColumn, metadataColumn);
        }));
    }

    private fixCustomFormatReferences(buildColumns: IGridViewColumn[], viewColumns: IGridViewColumn[]): void {
        buildColumns.forEach(buildColumn => {
            if (!buildColumn?.extraSettings?.customFormat) return;

            const pattern: RegExp = /\{(?<fieldName>[^}]+?)}/gm;

            buildColumn.extraSettings.customFormat = buildColumn.extraSettings.customFormat
                .replace(pattern, (match: string, fieldName: string): string => {

                    // If field name is not captured or this query alias is not missing, avoid making changes to this match.
                    if (!fieldName || buildColumns.some(column => column.queryAlias === fieldName)) return match;

                    const viewColumn: IGridViewColumn = viewColumns
                        .find(column => column.queryAlias === fieldName);

                    const similarColumn: IGridViewColumn = buildColumns
                        .find(column => viewColumn?.columnName?.localeCompare(column.columnName) === 0);

                    if (!viewColumn || !similarColumn) return match;

                    return `{${similarColumn.queryAlias}}`;
                });
        });
    }

    private createGridViewColumn(schemaColumn: IQuerySchemaColumn,
                                 similarColumn?: IGridViewColumn,
                                 metadataColumn?: IViewMetadataColumn): IGridViewColumn {

        const header: string = similarColumn?.header ?? metadataColumn?.title ?? schemaColumn.queryAlias;
        const visible: boolean = similarColumn?.visible ?? true;
        const sortable: boolean = similarColumn?.sortable ?? true;
        const dataType: DataType = schemaColumn.dataType ?? "string";

        const displayMode: DisplayMode = !!similarColumn?.displayMode && similarColumn.dataType === dataType
            ? similarColumn.displayMode
            : this.getDisplayModeByDataType(dataType);

        const inputType: InputType = !!similarColumn?.inputType && similarColumn.dataType === dataType
            ? similarColumn.inputType
            : this.getInputTypeByDataType(dataType);

        const footer: any = !!similarColumn?.footer && similarColumn.dataType === dataType
            ? similarColumn.footer : {};

        const extraSettings: any = !!similarColumn?.extraSettings && similarColumn.dataType === dataType
            ? similarColumn.extraSettings : {};

        return {
            // Set query alias and column name from query schema (cannot reuse the ones from similar column).
            queryAlias: schemaColumn.queryAlias,
            columnName: schemaColumn.baseColumnName ?? schemaColumn.columnName,
            // Set the remaining ones from similar column if exists, or from column metadata if not.
            header: header,
            dataType: dataType,
            footer: footer,
            visible: visible,
            sortable: sortable,
            inputType: inputType,
            displayMode: displayMode,
            extraSettings: extraSettings
        } as IGridViewColumn;
    }

    onCustomFormatModeChange(mode: number) {
        this.editViewColumn.extraSettings.customFormatMode = mode;
    }

    protected onRowReordered(): void {
        this.columns.forEach((column, index) => column.sortOrder = index);
    }

    protected async onGridViewColumnStartEditing(column: IGridViewColumn) {
        if (!column) return;

        if (column.extraSettings.customFormatMode === undefined) column.extraSettings.customFormatMode = 0;

        this.editViewColumn = <any>{
            extraSettings: {},
            footer: { textAlignment: "Left", leftCellLabelAlignment: "Right" }
        };

        deepUpdate(this.editViewColumn, column);

        if (this.editViewColumn.queryAlias?.endsWith(".ActionMenu")) {
            this.editViewColumn.extraSettings.enableActionButtons = true;
            this.editViewColumn.extraSettings.actionItems = [];

            this.loading = true;

            if (this.currentGridId) {
                this.widgetGridViewService
                    .getWidgetLinks(this.currentGridId)
                    .then(links => {
                        this.originalActionItems = links.map(l => ({
                            id: l.id,
                            name: l.linkName,
                            url: l.url
                        }));

                        this.editViewColumn.extraSettings.actionItems = this.originalActionItems
                            .map(o => ({ ...o }));
                    })
            } else {
                this.editViewColumn.extraSettings.actionItems = this.tempActionItems.map(o => ({ ...o }));
            }

            this.loading = false;
        }

        this.setFooterAggregateFunctionOptions();
        this.setFooterAggregateQueryColumnOptions();

        this.editGridViewColumnDialogVisible = true;
    }

    protected onGridViewColumnEditingDialogHide(): void {
        this.editViewColumn = null;
    }

    protected onViewColumnChanged(): void {
        if (!this.editViewColumn) return;

        const column: IGridViewColumn = this.columns
            .find(column => column.queryAlias === this.editViewColumn.queryAlias);

        if (column) {
            // Check if customFormat is not null and decode it
            this.editViewColumn.extraSettings.customFormat =
                this.editViewColumn.extraSettings?.customFormatMode === 1 &&
                !!this.editViewColumn.extraSettings?.customFormat
                    ? this.editViewColumn.extraSettings.customFormat.replace(/&lt;/g, "<").replace(/&gt;/g, ">")
                    : null;

            deepUpdate(column, this.editViewColumn);

            column.extraSettings.enableActionButtons = this.editViewColumn.extraSettings.enableActionButtons ?? false;
            column.extraSettings.actionItems = this.editViewColumn.extraSettings.actionItems ?? [];

            const isDraft = !this.currentGridId;
            const edited = this.editViewColumn.extraSettings.actionItems as Array<{
                id?: string;
                name: string;
                url: string
            }>;

            if (this.editViewColumn.queryAlias?.endsWith(".ActionMenu")) {
                if (isDraft) {
                    this.tempActionItems = [...edited];
                    this.editGridViewColumnDialogVisible = false;
                    return;
                }

                const original = this.originalActionItems;

                const toAdd = edited.filter(item => !item.id)
                    .map(item => ({
                        linkName: item.name,
                        url: item.url,
                        widgetGridId: this.currentGridId
                    }));

                const toUpdate = edited
                    .filter(item => item.id)
                    .filter(item => {
                        const orig = original.find(o => o.id === item.id);
                        return orig && (orig.name !== item.name || orig.url !== item.url);
                    })
                    .map(item => ({
                        id: item.id,
                        linkName: item.name,
                        url: item.url,
                        widgetGridId: this.currentGridId
                    }));

                const toDelete = original
                    .filter(o => !edited.find(e => e.id === o.id))
                    .map(o => o.id);

                if (!toAdd.length && !toUpdate.length && !toDelete.length) {
                    this.editGridViewColumnDialogVisible = false;
                    return;
                }

                this.loading = true;
                const calls: Promise<any>[] = [];

                if (toAdd.length) {
                    calls.push(this.widgetGridBuilderService.saveLinks(toAdd));
                }
                if (toUpdate.length) {
                    calls.push(...toUpdate.map(u => this.widgetGridBuilderService.updateLink(u)));
                }

                Promise.all(calls)
                    .finally(() => {
                        this.loading = false;
                        this.editGridViewColumnDialogVisible = false;
                    });
            } else {
                this.editGridViewColumnDialogVisible = false;
            }
        }
    }

    private setFooterAggregateFunctionOptions(): void {
        const aggregateFunctionOptions: SelectItem[] = [
            { label: "Sum", value: "sum" },
            { label: "Average", value: "avg" },
            { label: "Minimum", value: "min" },
            { label: "Maximum", value: "max" }
        ];
        const footerAggregationValue: string[] = this.editViewColumn.footer?.expressions ?? [];

        // Filter from the options the expression already taken.
        this.footerAggregateFunctionOptions = aggregateFunctionOptions
            .filter(item => !footerAggregationValue.some(value => item.value === value));
    }

    private setFooterAggregateQueryColumnOptions(): void {
        this.footerAggregateColumnOptions = [
            { label: "column", value: this.editViewColumn.queryAlias },
            ...this.columns
                .filter(column => column.queryAlias !== this.editViewColumn.queryAlias)
                .map(column => <SelectItem>{ label: column.queryAlias, value: column.queryAlias })
        ];
    }

    // Column extra settings editing methods.
    protected onCustomColumnTypeMaskChanged(data: IGridViewColumn, columnTypeId: string): void {
        if (!columnTypeId || !this.viewMetadata?.customColumnTypes) return;

        data.extraSettings.mask = this.viewMetadata.customColumnTypes
            .find(type => type.id == columnTypeId)?.mask;
    }

    protected onFooterAggregateFunctionSelected(value: string): void {
        if (!value) return;

        this.addAggregationExpression(value);
        this.setFooterAggregateFunctionOptions();
    }

    protected onCustomAggregateExpressionSubmitted(): void {
        this.addAggregationExpression(this.editAggregateExpression);
    }

    private addAggregationExpression(value: string): void {
        if (!this.editViewColumn) return;
        if (!this.editViewColumn.footer) this.editViewColumn.footer = {};
        if (!this.editViewColumn.footer.expressions) this.editViewColumn.footer.expressions = [];

        if (this.selectedAggregateExpressionIndex != null) {
            this.editViewColumn.footer.expressions[this.selectedAggregateExpressionIndex] = value;
            this.selectedAggregateExpressionIndex = null;
        } else {
            this.editViewColumn.footer.expressions.push(value);
        }

        this.editAggregateExpression = null;
    }

    protected onAggregateExpressionItemClicked(index: number): void {
        if (this.isPredefinedAggregateFunction(this.editViewColumn.footer.expressions[index])) return;

        if (this.selectedAggregateExpressionIndex === index) {
            this.selectedAggregateExpressionIndex = null;
            this.editAggregateExpression = "";
        } else {
            this.selectedAggregateExpressionIndex = index;
            this.editAggregateExpression = this.editViewColumn.footer.expressions[index];
        }
    }

    protected onFooterAggregateExpressionRemove(index: number): void {
        this.editViewColumn.footer.expressions.splice(index, 1);
        this.setFooterAggregateFunctionOptions();
    }

    protected onCustomAggregationExpressionColumnSelected(expression: any): void {
        const option: SelectItem = this.footerAggregateColumnOptions
            .find(option => option.value === expression.value);

        if (option) {
            let pasteValue = option.label;
            const caretPos = this.getElementCaretPos(this.aggregateExpressionInput);
            if (!this.editAggregateExpression?.length || this.editAggregateExpression[caretPos - 1] !== "@") {
                pasteValue = "@" + pasteValue;
            }

            let resultStr = this.editAggregateExpression;
            resultStr = resultStr.slice(0, caretPos) + pasteValue + resultStr.slice(caretPos);
            this.editAggregateExpression = resultStr;

            this.overlayPanelColumnOptions.hide();
            this.aggregateExpressionInput.nativeElement.focus();

            const newCaretPos = caretPos + pasteValue.length;

            setTimeout(() =>
                this.aggregateExpressionInput.nativeElement.setSelectionRange(newCaretPos, newCaretPos), 10);

        } else {
            this.overlayPanelColumnOptions.hide();
            this.aggregateExpressionInput.nativeElement.focus();
        }
    }

    // Auxiliary methods.
    protected getGridColumnAggregationsDisplaying(footer: any): string {
        if (!footer?.expressions) return "";

        let result = footer.outputFormat || footer.expressions.map((_: any, index: number) => `[${index}]`).join(" / ");

        footer.expressions.forEach((expression: any, index: number) => result = result.replace(`[${index}]`, expression));

        return result;
    }

    protected getAlphabeticallySortedColumns(): IGridViewColumn[] {
        const columns: IGridViewColumn[] = [...this.columns];
        const comparer = (first: any, second: any) => first.queryAlias.localeCompare(second.queryAlias);
        return columns.sort(comparer);
    }

    protected getGridColumnVariableName(value: string): string {
        return `${this._widgetSource?.name ?? ""} ${value}`
            .replace(/[.\s]/gm, "_").toLowerCase();
    }

    private getDisplayModeByDataType(dataType: DataType): DisplayMode {
        switch (dataType) {
            case "bool":
                return DisplayMode.Conditional;
            case "date":
                return DisplayMode.Date;
            case "numeric":
                return DisplayMode.Number;
            default:
                return DisplayMode.Text;
        }
    }

    private getInputTypeByDataType(dataType: DataType): InputType {
        switch (dataType) {
            case "bool":
                return "checkbox";
            case "date":
                return "calendar";
            case "numeric":
                return "number";
            default:
                return "text";
        }
    }

    private getElementCaretPos(elRef: ElementRef) {
        const el = elRef.nativeElement;
        return el.selectionDirection == "backward" ? el.selectionStart : el.selectionEnd;
    }

    private isPredefinedAggregateFunction(value: string): boolean {
        return value === "min" ||
            value === "max" ||
            value === "avg" ||
            value === "sum"
    }

    private getGridBuild(): IGridView {
        return {
            id: this.gridView?.id,
            columns: this.columns,
            xlsSheetName: this.xlsSheetName,
            widgetSource: this._widgetSource,
            querySourceId: this.querySourceId,
            isRowSelectable: this.isRowSelectable,
            widgetSourceId: this._widgetSource?.id,
            defaultSortOrder: this.defaultSortOrder,
            summaryFooterVisible: this.summaryFooterVisible,
            defaultSortColumnAlias: this.defaultSortColumnAlias,
            showVisibleColumnsSelector: this.showVisibleColumnsSelector,
        };
    }

    // Edition methods.
    async saveQuery(): Promise<string> {
        if (!this.queryBuilder) return;

        // Save the query as draft.
        this.loading = true;

        // If this query is a draft, and it is not the query currently being used by this widget,
        // make the changes over the query.
        // Otherwise, create a new draft query for them.
        const editionFunc = (): Promise<string> =>
            this.gridView.querySourceId !== this.querySourceId
                ? this.queryBuilder?.saveDraft()
                : this.queryBuilder?.createDraft();

        return await editionFunc().finally(() => this.loading = false);
    }

    async createDraft(): Promise<string> {
        if (!this.columns?.length) return null;
        // If query saving fails, stop edition and return null to indicate error.
        if (this.queryBuilderDirty && !await this.saveQuery()) return null;

        const originalGridId = this.gridView?.id;

        if (originalGridId) {
            await this.flushActionButtonChanges(originalGridId);
        }

        const draftView = await this.syncActionsAndForkDraft();

        if (!draftView) return null;
        const draftId = draftView.id;

        await this.flushActionButtonChanges(draftId);

        if (originalGridId && originalGridId !== draftId) {
            await this.flushActionButtonChanges(originalGridId);
        }

        return draftView.widgetSourceId;
    }

    async releaseDraft(): Promise<string> {
        if (!this.widgetSourceId) return;

        const editionFunc = (grid: IGridView): Promise<IGridView> =>
            this.widgetGridBuilderService.update(grid.id, grid)
                .then(build => this.widgetGridBuilderService.releaseDraft(build.widgetSourceId)
                    .then(widgetSourceId => this.widgetGridBuilderService.getView(widgetSourceId)
                        .then(view => view as IGridView)));

        const build = await this.editGrid(editionFunc);

        return build.widgetSourceId;
    }

    async save(): Promise<string> {
        if (!this.columns?.length) return null;

        // If query saving fails, stop edition and return null to indicate error.
        if (this.queryBuilderDirty && !await this.saveQuery()) return null;

        const draftId = this.currentGridId;

        if (draftId) {
            await this.flushActionButtonChanges(draftId);
        }

        const editionFunc = (grid: IGridView): Promise<IGridView> =>
            !this.widgetSourceId
                ? this.widgetGridBuilderService.create(grid)
                : this.widgetGridBuilderService.update(grid.id, grid);

        const build = await this.editGrid(editionFunc);

        if (!build) return null;

        await this.flushActionButtonChanges(build.id);

        return build?.widgetSourceId ?? null;
    }

    private async editGrid(editionFunc: (grid: IGridView) => Promise<IGridView>): Promise<IGridView> {
        this.loading = true;
        const grid: IGridView = this.getGridBuild();

        const build: IGridView = await editionFunc(grid).catch(error => {
            this.messageService.add(Message.Error(`There was an error saving entity:\n${error.error}`));
            return null;
        });

        if (build != null) this.gridView = build;

        this.loading = false;
        return build;
    }

    protected copyColumnVariable(column: IGridViewColumn): void {
        const variableName: string = this.getGridColumnVariableName(column.queryAlias);
        this.clipboard.copy(variableName);
        this.messageService.add(Message.Info(`Variable name #${variableName} is copied to clipboard`));
    }

    removeAction(index: number, linkId: string) {
        if (this.currentGridId && linkId) {
            this.widgetGridBuilderService
                .deleteLink(linkId)
                .then(() => {
                    this.editViewColumn.extraSettings.actionItems.splice(index, 1);
                });

            this.deletedActionItemIds.push(linkId);
        } else {
            this.editViewColumn.extraSettings.actionItems.splice(index, 1);
            this.tempActionItems = [...this.editViewColumn.extraSettings.actionItems];
        }
    }

    private async syncActionsAndForkDraft(): Promise<IGridView> {
        const originalId = this.currentGridId;
        this.baseWidgetGridId = originalId;

        if (originalId) {
            const edited = this.tempActionItems ?? [];
            const original = this.originalActionItems ?? [];

            const toAdd = edited.filter(i => !i.id)
                .map(i => ({ linkName: i.name, url: i.url, widgetGridId: originalId }));
            const toUpd = edited.filter(i => i.id)
                .filter(i => {
                    const o = original.find(x => x.id === i.id);
                    return o && (o.name !== i.name || o.url !== i.url);
                })
                .map(i => ({ id: i.id, linkName: i.name, url: i.url, widgetGridId: originalId }));
            const toDelete = this.deletedActionItemIds || [];

            const calls: Promise<any>[] = [];
            if (toAdd.length) calls.push(this.widgetGridBuilderService.saveLinks(toAdd));
            if (toUpd.length) calls.push(...toUpd.map(u => this.widgetGridBuilderService.updateLink(u)));
            if (toDelete.length) calls.push(...toDelete.map(id => this.widgetGridBuilderService.deleteLink(id)));
            if (calls.length) await Promise.all(calls);

            const fresh = await this.widgetGridViewService.getWidgetLinks(originalId);
            this.originalActionItems = fresh.map(l => ({ id: l.id, name: l.linkName, url: l.url }));
            this.tempActionItems = [...this.originalActionItems];
            this.deletedActionItemIds = [];
        }

        const widgetSourceId = this.isDraftWidget
            ? this._widgetSource.releaseWidgetId
            : this.widgetSourceId;
        const editionFunc = (grid: IGridView) =>
            widgetSourceId
                ? this.widgetGridBuilderService.createDraft(grid, widgetSourceId)
                : this.widgetGridBuilderService.createDraft(grid);

        const draft = await this.editGrid(editionFunc);
        if (!draft) return null;
        const draftId = draft.id;

        if (!originalId) {
            this.baseWidgetGridId = draftId;
        }

        const all = this.tempActionItems ?? [];
        const toClone = all.map(i => ({
            linkName: i.name,
            url: i.url,
            widgetGridId: draftId
        }));
        if (toClone.length) {
            await this.widgetGridBuilderService.saveLinks(toClone);
        }

        const saved = await this.widgetGridViewService.getWidgetLinks(draftId);
        this.originalActionItems = saved.map(l => ({ id: l.id, name: l.linkName, url: l.url }));
        this.tempActionItems = [...this.originalActionItems];

        return draft;
    }


    private async flushActionButtonChanges(activeWidgetGridId: string): Promise<void> {
        if (!activeWidgetGridId && !this.baseWidgetGridId) {
            return;
        }

        const edited = this.tempActionItems || [];
        const original = this.originalActionItems || [];
        const toAdd = edited.filter(i => !i.id);
        const toUpdate = edited.filter(i => !!i.id)
            .filter(i => {
                const o = original.find(x => x.id === i.id);
                return o && (o.name !== i.name || o.url !== i.url);
            });

        const toDelete = this.deletedActionItemIds || [];

        const targetIds = [activeWidgetGridId];
        if (this.baseWidgetGridId && this.baseWidgetGridId !== activeWidgetGridId) {
            targetIds.push(this.baseWidgetGridId);
        }

        const calls: Promise<any>[] = [];

        for (const widgetGridId of targetIds) {
            if (toAdd.length) {
                calls.push(this.widgetGridBuilderService.saveLinks(
                    toAdd.map(item => ({
                        linkName: item.name,
                        url: item.url,
                        widgetGridId
                    }))
                ));
            }

            if (toUpdate.length) {
                calls.push(...toUpdate.map(item =>
                    this.widgetGridBuilderService.updateLink({
                        id: item.id,
                        linkName: item.name,
                        url: item.url,
                        widgetGridId
                    })
                ));
            }

            if (toDelete.length) {
                calls.push(...toDelete.map(linkId =>
                    this.widgetGridBuilderService.deleteLink(linkId)
                ));
            }
        }

        if (calls.length) {
            await Promise.all(calls);
        }

        const refetchId = this.baseWidgetGridId || activeWidgetGridId;
        const fresh = await this.widgetGridViewService.getWidgetLinks(refetchId);
        this.originalActionItems = fresh.map(l => ({ id: l.id, name: l.linkName, url: l.url }));
        this.tempActionItems = [...this.originalActionItems];
        this.deletedActionItemIds = [];
    }
}